import { Badge } from '@design/components/badge/Badge';
import { Chip } from '@design/components/chip/Chip';
import { Icon } from '@design/components/icon/Icon';
import clsx from 'clsx';
import { MouseEvent, ReactNode } from 'react';

import styles from './MentionChip.module.scss';

export const MENTION_CHIP_KINDS = ['entity', 'theme', 'watchlist'] as const;
export type MentionChipKind = (typeof MENTION_CHIP_KINDS)[number];

export interface MentionChipProps {
  kind: MentionChipKind;
  label: string;
  avatar?: ReactNode;
  readOnly?: boolean;
  isSelected?: boolean;
  className?: string;
  onClick?: (e: MouseEvent) => void;
  'data-testid'?: string;
}

const entityInitialGlyph = (label: string): ReactNode => {
  const initial = label.trim().charAt(0).toUpperCase() || '?';

  // CSS `content` keeps the letter out of textContent / copy / flattened a11y text.
  return (
    <span
      className={styles.entityInitial}
      aria-hidden
      data-initial={initial}
      data-testid="mention-chip-entity-initial"
    />
  );
};

const kindGlyph = (kind: MentionChipKind, label: string): ReactNode => {
  if (kind === 'entity') {
    return entityInitialGlyph(label);
  }

  if (kind === 'theme') {
    // Same fallback as ThemeLogoIcon when no logo is available.
    return entityInitialGlyph(label);
  }

  return (
    <span className={styles.watchlistGlyphWrap}>
      <Icon iconName="Watchlist" size={8} className={styles.watchlistGlyph} />
    </span>
  );
};

const kindClass: Record<MentionChipKind, string> = {
  entity: styles.entity,
  theme: styles.theme,
  watchlist: styles.watchlist,
};

export const MentionChip = ({
  kind,
  label,
  avatar,
  readOnly = false,
  isSelected = false,
  className,
  onClick,
  'data-testid': dataTestId,
}: MentionChipProps) => {
  const glyph = avatar ?? kindGlyph(kind, label);

  if (readOnly) {
    return (
      <Badge
        variant="textual"
        size="xsmall"
        label={label}
        leftSection={glyph}
        className={clsx(styles.readOnly, kindClass[kind], className)}
        data-testid={dataTestId ?? 'mention-chip-readonly'}
      />
    );
  }

  return (
    <Chip
      label={label}
      assetLogo={glyph}
      onClick={onClick}
      className={clsx(
        styles.interactive,
        kindClass[kind],
        { [styles.selected]: isSelected },
        className
      )}
      data-testid={dataTestId ?? 'mention-chip'}
    />
  );
};
